/*
  Station Atlas — interactive map behavior.
  Adapted from a standalone HTML/Tailwind export (Claude Projects/sub pages/final
  pages/meet-our-sheep-stations.html) for sections/meet-our-sheep-stations.liquid.

  Two changes from the original export:
  1. Image URLs are resolved against window.__SA_ASSETS_BASE__ (set inline by the
     section from Shopify's asset_url), since stationsData just holds bare filenames.
  2. The map is no longer a fullscreen 100vh/100vw element — it's a bounded box within
     the normal page (header/footer stay visible), so framing math that used to read
     window.innerWidth/innerHeight now reads the #app container's own bounding rect.
*/
(function () {
  const ASSETS_BASE = window.__SA_ASSETS_BASE__ || '';

  /* ================================================================
     DATA
  ================================================================ */
  const stationsData = [
    {
      id: "lake-hawea", num: "01",
      name: "Lake Hāwea Station",
      owners: "The Ross Family",
      coordinates: { lat: -44.6185, lng: 169.2716 },
      region: "Queenstown Lakes District, Otago",
      story: "Australasia's first certified carbon-zero farm. They embed rigorous, data-led science to map vegetation and measure carbon sequestered deep within their soil and pastures.",
      sheepFacts: {
        flockSize: "10,000 Merino Sheep",
        micron: "16 to 18 μm (Extra Fine)",
        welfare: "SPCA Certified, rotational holistic grazing, planted 27,000+ native trees."
      },
      photo: "lakehawea.jpg",
      sticker: "lake-hawea-sticker.png",
      climate: { base: 3, windBase: 22, labels: ["Crisp Alpine Winds", "Clear Lake Light", "High Country Frost"] }
    },
    {
      id: "middlehurst", num: "02",
      name: "Middlehurst Station",
      owners: "The Macdonald Family",
      coordinates: { lat: -41.9892, lng: 173.4475 },
      region: "Upper Awatere Valley, Marlborough",
      story: "One of New Zealand’s original high-country runs, spanning rugged alpine terrain up to 2,500 meters. Focused on the architecture of longevity and modernizing high-country stewardship.",
      sheepFacts: {
        flockSize: "11,000 Merino Sheep",
        micron: "19.5 μm (High Performance)",
        welfare: "ZQ Certified, ethical free-range grazing, hand-sorted fleece grading."
      },
      photo: "middlehurst.jpg",
      sticker: "middlehurst-sticker.png",
      climate: { base: 2, windBase: 28, labels: ["Rugged Valley Gusts", "Clear Mountain Skies", "Alpine Stillness"] }
    },
    {
      id: "omarama", num: "03",
      name: "Omarama Station",
      owners: "The Subtil Family",
      coordinates: { lat: -44.4917, lng: 169.9567 },
      region: "Mackenzie Country, Canterbury",
      story: "Māori for 'Place of Light'. A magnificent 12,000-hectare property deep in ecosystem restoration. Their water leaves the farm cleaner than it enters, thanks to holistic filtration management.",
      sheepFacts: {
        flockSize: "16,500 Merino Sheep",
        micron: "19.5 μm (Naturally Odor-Resistant)",
        welfare: "Muster via traditional dog teams, dedicated longfin eel recovery program."
      },
      photo: "omarama.jpg",
      sticker: "omarama-sticker.png",
      climate: { base: 1, windBase: 18, labels: ["Soft Southern Light", "Mackenzie Starlight Chill", "Clear Basin Skies"] }
    }
  ];

  /* ================================================================
     PROJECTION  (lat/lng ⇄ viewBox units)
  ================================================================ */
  const VB_W = 860, VB_H = 940;
  const KM_PER_UNIT = 0.846; // ≈ at 44°S
  const projectPt = (lat, lng) => ({
    x: 30 + ((lng - 166) / 8.5) * 800,
    y: 20 + ((-40.4 - lat) / 6.4) * 900
  });
  const unproject = (x, y) => ({
    lng: 166 + ((x - 30) / 800) * 8.5,
    lat: -40.4 - ((y - 20) / 900) * 6.4
  });
  const stations = stationsData.map(s => ({ ...s, ...projectPt(s.coordinates.lat, s.coordinates.lng) }));
  const stationsById = {};
  stations.forEach(st => stationsById[st.id] = st);

  const LAKES = [
    { d: "M 244 668 C 252 646, 262 640, 258 660 C 254 676, 264 682, 256 694 C 248 704, 238 690, 244 668 Z", label: "L. Wakatipu", lx: 262, ly: 690 },
    { d: "M 388 540 C 396 522, 404 522, 402 540 C 400 556, 392 562, 388 554 C 384 548, 384 548, 388 540 Z", label: "L. Pukaki", lx: 406, ly: 548 },
    { d: "M 330 590 C 336 574, 344 574, 342 590 C 340 604, 334 610, 330 602 C 326 596, 326 596, 330 590 Z", label: "L. Hāwea", lx: 322, ly: 612 }
  ];

  /* Mountain glyph positions along the Southern Alps — pulled inland, clear of the coast */
  const PEAKS = [ [286,540],[336,500],[384,458],[430,418],[478,378],[526,338],[568,296],[604,256],[210,648],[248,602] ];

  /* Trees */
  const TREES = [
    [356,640],[372,628],[318,646],[302,628],[352,570],[430,618],[418,566],[444,588],
    [712,268],[748,262],[726,290],[700,222],
    [560,470],[590,440],[520,520],[620,388],[180,700],[210,724],[560,140],[588,120]
  ];

  /* Sheep flocks — clustered around the three stations + strays */
  const SHEEP_SPOTS = [];
  const FLOCK_OFFSETS = [[-22,12],[16,9],[-7,-16],[24,-7],[3,20]];
  stations.forEach(st => FLOCK_OFFSETS.forEach(([dx,dy],i) => SHEEP_SPOTS.push({ x: st.x+dx, y: st.y+dy, id: st.id+"-"+i })));
  SHEEP_SPOTS.push({ x: 520, y: 434, id: "stray-1" }, { x: 606, y: 156, id: "stray-2" }, { x: 300, y: 700, id: "stray-3" });

  const GRID_EXT = 2800;
  const FULL_VB = { x: 0, y: 0, w: VB_W, h: VB_H };
  const INTRO_VB = { x: -VB_W, y: -VB_H, w: VB_W * 3, h: VB_H * 3 };

  /* ================================================================
     AUDIO
  ================================================================ */
  const baaAudioEl = document.getElementById("baaAudio");
  function playBaa(){ try { baaAudioEl.currentTime = 0; baaAudioEl.play(); } catch(e){} }
  const BAA_REGISTRY = [];

  /* ================================================================
     LIVE TIME + SIMULATED WEATHER
  ================================================================ */
  function nzParts(){
    const now = new Date();
    const fmt = (opt) => new Intl.DateTimeFormat("en-NZ", { timeZone: "Pacific/Auckland", ...opt }).format(now);
    return {
      time: fmt({ hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false }),
      date: fmt({ weekday: "short", day: "numeric", month: "short" }).toUpperCase(),
      hourNZ: parseInt(fmt({ hour: "numeric", hour12: false }), 10)
    };
  }
  function weatherFor(st, hourNZ){
    const seed = st.id.length * 7 + hourNZ;
    const diurnal = Math.sin(((hourNZ - 4) / 24) * Math.PI * 2) * 4;
    return {
      temp: Math.round(st.climate.base + diurnal),
      wind: Math.round(st.climate.windBase + (seed % 9) - 4),
      label: st.climate.labels[seed % st.climate.labels.length]
    };
  }

  /* ================================================================
     EASING — cubic-bezier solver (mirrors framer-motion's curves)
  ================================================================ */
  function BezierEasing(mX1, mY1, mX2, mY2){
    function A(a1,a2){ return 1 - 3*a2 + 3*a1; }
    function B(a1,a2){ return 3*a2 - 6*a1; }
    function C(a1){ return 3*a1; }
    function calcBezier(t,a1,a2){ return ((A(a1,a2)*t + B(a1,a2))*t + C(a1))*t; }
    function getSlope(t,a1,a2){ return 3*A(a1,a2)*t*t + 2*B(a1,a2)*t + C(a1); }
    function getTForX(x){
      let guess = x;
      for (let i=0;i<6;i++){
        const slope = getSlope(guess, mX1, mX2);
        if (slope === 0) return guess;
        const cx = calcBezier(guess, mX1, mX2) - x;
        guess -= cx / slope;
      }
      return guess;
    }
    return function(x){
      if (x <= 0) return 0;
      if (x >= 1) return 1;
      return calcBezier(getTForX(x), mY1, mY2);
    };
  }
  const easeFlyTo = BezierEasing(0.16, 1, 0.3, 1);
  const easeInOut = BezierEasing(0.42, 0, 0.58, 1);
  function sleep(ms){ return new Promise(r => setTimeout(r, ms)); }

  /* ================================================================
     STATIC MAP DECOR
  ================================================================ */
  function buildStaticDecor(){
    let g = "";
    for (let lng = 158; lng <= 184; lng++){
      const { x } = projectPt(-43, lng);
      g += `<line x1="${x}" y1="${-GRID_EXT}" x2="${x}" y2="${VB_H + GRID_EXT}" stroke="var(--grid)" stroke-width="1" vector-effect="non-scaling-stroke" />`;
    }
    for (let lat = -34; lat >= -53; lat--){
      const { y } = projectPt(lat, 170);
      g += `<line x1="${-GRID_EXT}" y1="${y}" x2="${VB_W + GRID_EXT}" y2="${y}" stroke="var(--grid)" stroke-width="1" vector-effect="non-scaling-stroke" />`;
    }
    document.getElementById("graticule").innerHTML = g;

    let lakesHtml = "";
    LAKES.forEach(l => {
      lakesHtml += `<g><path d="${l.d}" fill="var(--lake)" stroke="var(--ink)" stroke-width="0.7" vector-effect="non-scaling-stroke" /><text x="${l.lx}" y="${l.ly}" style="font:italic 400 8px var(--mono);fill:var(--ink-3)">${l.label}</text></g>`;
    });
    document.getElementById("lakesLayer").innerHTML = lakesHtml;

    let peaksHtml = "";
    PEAKS.forEach(([x,y]) => {
      peaksHtml += `<g transform="translate(${x} ${y})" opacity="0.9">
        <path d="M -9 0 L 0 -14 L 9 0 Z" fill="var(--paper)" stroke="var(--ink)" stroke-width="0.9" stroke-linejoin="round" />
        <path d="M -3 -9.4 L 0 -14 L 3 -9.4 L 1.4 -8 L 0 -9.4 L -1.4 -8 Z" fill="var(--ink)" />
      </g>`;
    });
    document.getElementById("peaksLayer").innerHTML = peaksHtml;

    let treesHtml = "";
    TREES.forEach(([x,y], i) => {
      const delay = (i % 5) * 0.7;
      treesHtml += `<g transform="translate(${x} ${y})"><g class="tree" style="animation-delay:${delay}s">
        <line x1="0" y1="0" x2="0" y2="-4" stroke="var(--tree)" stroke-width="1.4" />
        <path d="M -4.5 -3 L 0 -12 L 4.5 -3 Z" fill="var(--tree)" />
        <path d="M -3.4 -8 L 0 -15.5 L 3.4 -8 Z" fill="var(--tree)" />
      </g></g>`;
    });
    document.getElementById("treesLayer").innerHTML = treesHtml;

    let ticksHtml = "";
    for (let i = 0; i < 24; i++){
      const a = (i / 24) * Math.PI * 2;
      const r1 = i % 6 === 0 ? 33 : 35.5, r2 = 38;
      const x1 = 42 + Math.cos(a) * r1, y1 = 42 + Math.sin(a) * r1;
      const x2 = 42 + Math.cos(a) * r2, y2 = 42 + Math.sin(a) * r2;
      const sw = i % 6 === 0 ? 1.2 : 0.6;
      ticksHtml += `<line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" stroke="var(--ink)" stroke-width="${sw}" />`;
    }
    document.getElementById("compassTicks").innerHTML = ticksHtml;
  }

  /* ================================================================
     SHEEP
  ================================================================ */
  const sheepStates = {};

  function sheepMarkup(spot, flip, i){
    const delay = (0.8 + i * 0.08).toFixed(2);
    return `<g class="sheep-hit" data-id="${spot.id}" style="transition-delay:${delay}s" transform="translate(${spot.x} ${spot.y})">
      <g class="sheep-flip"${flip ? ' transform="scale(-1 1)"' : ""}>
        <g class="sheep-body">
          <line x1="-5.2" y1="3" x2="-5.9" y2="8" stroke="var(--ink)" stroke-width="1.1" stroke-linecap="round" />
          <line x1="-2.4" y1="3.6" x2="-2.6" y2="8.2" stroke="var(--ink)" stroke-width="1.1" stroke-linecap="round" />
          <line x1="2.8" y1="3.6" x2="3" y2="8.2" stroke="var(--ink)" stroke-width="1.1" stroke-linecap="round" />
          <line x1="5.6" y1="3" x2="6.3" y2="8" stroke="var(--ink)" stroke-width="1.1" stroke-linecap="round" />
          <path d="M -9.5 2.8
                   C -12.5 2.6, -13 -1.2, -10.5 -2.6
                   C -11.5 -5.8, -8.2 -8, -5.6 -6.6
                   C -4.8 -9.4, -1.2 -10, 0.8 -8
                   C 2.8 -10, 6.4 -9, 6.8 -6.2
                   C 10 -6.6, 12 -3.6, 9.8 -1.4
                   C 11.6 1, 9.4 3.4, 6.6 2.8
                   C 5 4.4, 1.5 4.6, 0 3.4
                   C -2 4.8, -5 4.8, -6.2 3.2
                   C -7.4 4, -8.8 3.6, -9.5 2.8 Z"
            fill="white" stroke="var(--ink)" stroke-width="1" stroke-linejoin="round" />
          <g class="sheep-head">
            <ellipse cx="-10.6" cy="-1.8" rx="2.6" ry="3.7" fill="var(--ink)" transform="rotate(16 -10.6 -1.8)" />
            <ellipse cx="-13" cy="-4.4" rx="1.9" ry="0.9" fill="var(--ink)" transform="rotate(-32 -13 -4.4)" />
            <ellipse cx="-8.6" cy="-5" rx="1.9" ry="0.9" fill="var(--ink)" transform="rotate(-8 -8.6 -5)" />
            <circle cx="-10" cy="-5.4" r="1.9" fill="white" stroke="var(--ink)" stroke-width="0.7" />
            <circle cx="-11.6" cy="-2.6" r="0.6" fill="white" />
          </g>
          <path d="M 9.2 -1.6 C 11.4 -3.4, 13.2 -1.6, 12 0.2 C 12.8 1.6, 10.8 2.6, 9.8 1.4 Z"
            fill="white" stroke="var(--ink)" stroke-width="0.8" stroke-linejoin="round" />
        </g>
      </g>
      <g class="baa-bubble">
        <rect x="2" y="-24" width="34" height="15" rx="7.5" fill="var(--yellow)" stroke="var(--ink)" stroke-width="1" />
        <path d="M 8 -9.5 L 5 -3.5 L 13 -9" fill="var(--yellow)" stroke="var(--ink)" stroke-width="1" stroke-linejoin="round" />
        <text x="19" y="-13" text-anchor="middle" style="font:700 8.5px var(--mono);fill:var(--ink)">BAA!</text>
      </g>
    </g>`;
  }

  function buildSheep(){
    const layer = document.getElementById("sheepLayer");
    let html = "";
    SHEEP_SPOTS.forEach((spot, i) => {
      const flip = Math.random() < 0.5;
      sheepStates[spot.id] = { id: spot.id, spot, x: spot.x, y: spot.y, flip, baaTimer: null, jumpTimer: null };
      html += sheepMarkup(spot, flip, i);
    });
    layer.innerHTML = html;
    Object.values(sheepStates).forEach(st => BAA_REGISTRY.push(() => doBaa(st)));
    requestAnimationFrame(() => {
      layer.querySelectorAll(".sheep-hit").forEach(el => el.classList.add("visible"));
    });
    Object.values(sheepStates).forEach(st => wanderLoop(st));
  }

  function doBaa(state){
    playBaa();
    const hit = document.querySelector(`.sheep-hit[data-id="${state.id}"]`);
    if (!hit) return;
    const body = hit.querySelector(".sheep-body");
    const bubble = hit.querySelector(".baa-bubble");
    if (body){
      body.classList.remove("sheep-jump");
      void body.offsetWidth; // restart animation
      body.classList.add("sheep-jump");
      clearTimeout(state.jumpTimer);
      state.jumpTimer = setTimeout(() => body.classList.remove("sheep-jump"), 700);
    }
    if (bubble){
      bubble.classList.add("show");
      clearTimeout(state.baaTimer);
      state.baaTimer = setTimeout(() => bubble.classList.remove("show"), 950);
    }
  }

  async function wanderLoop(state){
    await sleep(1500 + Math.random() * 3000);
    while (true){
      const nx = state.spot.x + (Math.random() - 0.5) * 46;
      const ny = state.spot.y + (Math.random() - 0.5) * 28;
      const newFlip = nx < state.x;
      if (newFlip !== state.flip){
        state.flip = newFlip;
        const flipEl = document.querySelector(`.sheep-hit[data-id="${state.id}"] .sheep-flip`);
        if (flipEl) flipEl.setAttribute("transform", state.flip ? "scale(-1 1)" : "");
      }
      const dur = 2.6 + Math.random() * 2.4;
      tweenSheep(state, nx, ny, dur);
      await sleep(dur * 1000 + 600 + Math.random() * 2800);
    }
  }

  function tweenSheep(state, nx, ny, dur){
    const hit = document.querySelector(`.sheep-hit[data-id="${state.id}"]`);
    if (!hit) return;
    const fromX = state.x, fromY = state.y;
    const start = performance.now();
    function frame(now){
      const t = Math.min((now - start) / (dur * 1000), 1);
      const et = easeInOut(t);
      state.x = fromX + (nx - fromX) * et;
      state.y = fromY + (ny - fromY) * et;
      hit.setAttribute("transform", `translate(${state.x} ${state.y})`);
      if (t < 1) requestAnimationFrame(frame);
    }
    requestAnimationFrame(frame);
  }

  /* ================================================================
     PINS
  ================================================================ */
  function pinMarkup(st, i){
    const delay = (0.3 + i * 0.2).toFixed(2);
    const labelWidth = st.name.length * 6.4 + 14;
    return `<g class="pin" data-id="${st.id}" style="transition-delay:${delay}s">
      <circle class="pin-ring" cx="${st.x}" cy="${st.y}" r="13" fill="none" stroke="var(--accent)" stroke-width="1.6" />
      <g class="pin-badge">
        <circle cx="${st.x}" cy="${st.y}" r="10" fill="var(--ink)" stroke="var(--paper)" stroke-width="2" />
        <text x="${st.x}" y="${st.y + 3}" text-anchor="middle" style="font:600 8px var(--mono);fill:var(--paper)">${st.num}</text>
      </g>
      <line x1="${st.x + 11}" y1="${st.y - 8}" x2="${st.x + 26}" y2="${st.y - 20}" stroke="var(--ink)" stroke-width="0.8" vector-effect="non-scaling-stroke" />
      <rect x="${st.x + 24}" y="${st.y - 34}" width="${labelWidth}" height="17" fill="var(--paper)" stroke="var(--ink)" stroke-width="0.8" vector-effect="non-scaling-stroke" />
      <text x="${st.x + 31}" y="${st.y - 22.5}" style="font:500 9px var(--mono);fill:var(--ink);letter-spacing:0.08em">${st.name.toUpperCase()}</text>
    </g>`;
  }

  function buildPins(){
    const layer = document.getElementById("pinsLayer");
    let html = "";
    stations.forEach((st, i) => { html += pinMarkup(st, i); });
    layer.innerHTML = html;
    requestAnimationFrame(() => {
      layer.querySelectorAll(".pin").forEach(el => el.classList.add("visible"));
    });
  }

  function updatePinDimming(){
    document.querySelectorAll(".pin").forEach(el => {
      if (!active){ el.style.opacity = ""; return; }
      el.style.opacity = (el.dataset.id === active.id) ? "1" : "0.45";
    });
  }

  /* ================================================================
     DETAIL PANEL
  ================================================================ */
  function factRow(label, value){
    return `<div class="sa-fact-row">
      <p class="sa-fact-label">${label}</p>
      <p class="sa-fact-value">${value}</p>
    </div>`;
  }

  function renderPanelContent(st){
    const panel = document.getElementById("detailPanel");
    const parts = st.region.split(",");
    const figRegion = parts[1] ? parts[1].trim().toUpperCase() : st.region.toUpperCase();
    panel.innerHTML = `
      <div id="panelInner">
        <div class="sa-panel-photo">
          <img class="sa-photo" src="${ASSETS_BASE}${st.photo}" alt="${st.name}, ${st.region} — Sheep Inc. Merino wool station" width="470" height="240" loading="lazy" decoding="async" />
          <img class="sa-panel-sticker" src="${ASSETS_BASE}${st.sticker}" alt="${st.name} souvenir sticker" loading="lazy" decoding="async" />
          <div class="sa-panel-fig">
            FIG. ${st.num} — ${figRegion}
          </div>
        </div>
        <div class="sa-panel-body">
          <span class="sa-eyebrow">Station ${st.num} / 03</span>
          <h2 class="sa-station-name">${st.name}</h2>
          <p class="sa-owners">${st.owners}</p>
          <p class="sa-region">${st.region}</p>
          <p class="sa-story">${st.story}</p>
          <div class="sa-stats">
            <div class="sa-stats-cell sa-stats-cell--bordered">
              <p class="sa-stats-label">Local Time · NZST</p>
              <p id="panelTime">--:--:--</p>
              <p id="panelDate"></p>
            </div>
            <div class="sa-stats-cell">
              <p class="sa-stats-label">Conditions</p>
              <p>
                <span id="panelWxTemp">--°C</span> <span id="panelWxWind"></span>
              </p>
              <p id="panelWxLabel"></p>
            </div>
          </div>
          ${factRow("The Flock", st.sheepFacts.flockSize)}
          ${factRow("Fibre", st.sheepFacts.micron)}
          ${factRow("Welfare &amp; Land", st.sheepFacts.welfare)}
          <p class="sa-coords-footer">
            ${Math.abs(st.coordinates.lat).toFixed(4)}°S / ${st.coordinates.lng.toFixed(4)}°E — TE WAI POUNAMU
          </p>
        </div>
      </div>
    `;
  }

  let clockTimer = null;
  function updateClock(st){
    const { time, date, hourNZ } = nzParts();
    const wx = weatherFor(st, hourNZ);
    const elTime = document.getElementById("panelTime");
    if (!elTime) return;
    elTime.textContent = time;
    document.getElementById("panelDate").textContent = date;
    document.getElementById("panelWxTemp").textContent = `${wx.temp}°C`;
    document.getElementById("panelWxWind").textContent = `/ ${wx.wind}km/h`;
    document.getElementById("panelWxLabel").textContent = wx.label.toUpperCase();
  }

  function showPanel(st){
    const panel = document.getElementById("detailPanel");
    const wasOpen = panel.classList.contains("open");
    const doRender = () => {
      renderPanelContent(st);
      if (clockTimer) clearInterval(clockTimer);
      updateClock(st);
      clockTimer = setInterval(() => updateClock(st), 1000);
    };
    if (wasOpen){
      const inner = document.getElementById("panelInner");
      if (inner) inner.style.opacity = "0";
      setTimeout(doRender, 180);
    } else {
      panel.classList.add("open");
      doRender();
    }
  }

  function hidePanel(){
    const panel = document.getElementById("detailPanel");
    panel.classList.remove("open");
    if (clockTimer){ clearInterval(clockTimer); clockTimer = null; }
  }

  /* ================================================================
     APP STATE + VIEWBOX CONTROL
  ================================================================ */
  let active = null;
  let currentVB = { ...INTRO_VB };
  let vbGen = 0;
  let dragState = null;
  const svg = document.getElementById("mapSvg");
  const appEl = document.getElementById("app");

  function applyVB(v){
    currentVB = v;
    svg.setAttribute("viewBox", `${v.x} ${v.y} ${v.w} ${v.h}`);
    updateScaleBar(v);
  }

  function updateScaleBar(v){
    const bar = document.getElementById("scaleBar");
    const txt = document.getElementById("scaleTxt");
    if (!bar || !txt) return;
    const rect = svg.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const pxPerUnit = Math.min(rect.width / v.w, rect.height / v.h);
    const rawKm = (120 / pxPerUnit) * KM_PER_UNIT;
    const nice = [1,2,5,10,20,25,50,100,200].reduce((p,c) => Math.abs(c - rawKm) < Math.abs(p - rawKm) ? c : p);
    bar.style.width = `${(nice / KM_PER_UNIT) * pxPerUnit}px`;
    txt.textContent = `${nice} KM`;
  }

  function flyTo(target, dur = 1.7){
    const myGen = ++vbGen;
    const from = { ...currentVB };
    const start = performance.now();
    function frame(now){
      if (myGen !== vbGen) return;
      const t = Math.min((now - start) / (dur * 1000), 1);
      const et = easeFlyTo(t);
      applyVB({
        x: from.x + (target.x - from.x) * et,
        y: from.y + (target.y - from.y) * et,
        w: from.w + (target.w - from.w) * et,
        h: from.h + (target.h - from.h) * et
      });
      if (t < 1) requestAnimationFrame(frame);
    }
    requestAnimationFrame(frame);
  }

  function selectStation(st){
    const rect = svg.getBoundingClientRect();
    const vw = rect.width || window.innerWidth, vh = rect.height || window.innerHeight;
    const mobile = window.innerWidth < 768;
    const w = mobile ? 300 : 280;
    const hAdj = w * vh / vw;
    const fx = mobile ? 0.5 : ((vw - 470) / 2) / vw;
    const fy = mobile ? 0.24 : 0.52;
    flyTo({ x: st.x - w * fx, y: st.y - hAdj * fy, w, h: hAdj }, 1.7);
    active = st;
    appEl.classList.add("has-active");
    updatePinDimming();
    showPanel(st);
  }

  function resetView(){
    active = null;
    appEl.classList.remove("has-active");
    flyTo(FULL_VB, 1.5);
    updatePinDimming();
    hidePanel();
  }

  function onMove(e){
    const coords = document.getElementById("coordsTxt");
    if (!coords) return;
    const r = svg.getBoundingClientRect();
    const v = currentVB;
    const sf = Math.min(r.width / v.w, r.height / v.h);
    const ox = (r.width - v.w * sf) / 2, oy = (r.height - v.h * sf) / 2;
    const ux = v.x + (e.clientX - r.left - ox) / sf;
    const uy = v.y + (e.clientY - r.top - oy) / sf;
    const { lat, lng } = unproject(ux, uy);
    coords.textContent = `${Math.abs(lat).toFixed(4)}°S / ${lng.toFixed(4)}°E`;
  }

  function onPointerDown(e){
    if (currentVB.w >= VB_W * 0.95) return;
    dragState = { sx: e.clientX, sy: e.clientY, vb: { ...currentVB }, moved: false };
  }
  function onPointerMove(e){
    if (!dragState) return;
    const dx = e.clientX - dragState.sx, dy = e.clientY - dragState.sy;
    if (Math.abs(dx) + Math.abs(dy) > 3) dragState.moved = true;
    if (!dragState.moved) return;
    const r = svg.getBoundingClientRect();
    const v = dragState.vb;
    const sf = Math.min(r.width / v.w, r.height / v.h);
    applyVB({ ...v, x: v.x - dx / sf, y: v.y - dy / sf });
  }
  function onPointerUp(){ dragState = null; }

  function onKey(e){
    if (e.key === "Escape") resetView();
    if (e.key.toLowerCase() === "b" && BAA_REGISTRY.length){
      BAA_REGISTRY[Math.floor(Math.random() * BAA_REGISTRY.length)]();
    }
  }

  function enterReadyPhase(){
    appEl.classList.add("is-ready");
    buildSheep();
    buildPins();
  }

  /* ================================================================
     HEADER HEIGHT SYNC
     #app fills the viewport down to the fold (see --sa-header-h in the CSS),
     so it needs the theme header's real rendered height — including the
     announcement bar, which can rotate between messages of different
     line-heights. Measured live (not hardcoded) and kept in sync via
     ResizeObserver so it never leaves a gap the footer peeks through.
  ================================================================ */
  function syncHeaderHeight(){
    const root = document.querySelector(".station-atlas");
    const header = document.querySelector(".header-wrapper");
    if (!root) return;
    const apply = () => {
      root.style.setProperty("--sa-header-h", `${header ? header.getBoundingClientRect().height : 0}px`);
    };
    apply();
    if (header && "ResizeObserver" in window) {
      new ResizeObserver(apply).observe(header);
    } else {
      window.addEventListener("resize", apply);
    }
  }

  /* ================================================================
     INIT
  ================================================================ */
  function init(){
    syncHeaderHeight();
    buildStaticDecor();
    applyVB({ ...INTRO_VB });

    setTimeout(() => flyTo(FULL_VB, 3.0), 400);
    setTimeout(() => enterReadyPhase(), 3300);

    window.addEventListener("resize", () => updateScaleBar(currentVB));
    window.addEventListener("keydown", onKey);

    svg.addEventListener("mousemove", onMove);
    svg.addEventListener("pointerdown", onPointerDown);
    svg.addEventListener("pointermove", onPointerMove);
    svg.addEventListener("pointerup", onPointerUp);
    svg.addEventListener("pointerleave", onPointerUp);

    document.getElementById("pinsLayer").addEventListener("click", (e) => {
      const pinEl = e.target.closest(".pin");
      if (!pinEl) return;
      e.stopPropagation();
      const st = stationsById[pinEl.dataset.id];
      if (st) selectStation(st);
    });

    document.getElementById("sheepLayer").addEventListener("click", (e) => {
      const hit = e.target.closest(".sheep-hit");
      if (!hit) return;
      e.stopPropagation();
      const st = sheepStates[hit.dataset.id];
      if (st) doBaa(st);
    });

    document.getElementById("compassBtn").addEventListener("click", () => {
      const needle = document.getElementById("compassNeedle");
      needle.classList.add("spin");
      setTimeout(() => needle.classList.remove("spin"), 1200);
    });

    document.getElementById("backBtn").addEventListener("click", resetView);
  }

  init();
})();
